/**
 * Every date and time Pulse shows, in London.
 *
 * NetXL is a UK company working to London hours, so London is simply what the
 * clock on the wall says. Stating it once here rather than inheriting whatever
 * the browser happens to be set to means a machine with a misconfigured zone --
 * a fresh VM left on UTC, a laptop that picked something up on a trip -- shows
 * the same times as everybody else's rather than being quietly an hour out.
 *
 * The rest of the stack already worked this way: `packages/sla/src/calendar.ts`
 * computes business hours in Europe/London, and the dashboard's date ranges are
 * bucketed in it. This is the display half of a decision already taken -- before
 * it, an SLA could be measured against London and then rendered against
 * something else.
 *
 * **Europe/London is not a fixed offset.** It is UTC in winter and UTC+1 in
 * summer, so this cannot be done by adding an hour anywhere; the zone name goes
 * to `Intl` and the platform applies the right rule per instant. A date in
 * January and one in July format correctly on the same screen.
 *
 * The locale is pinned alongside it for the same reason -- the desk is British,
 * and en-GB is what makes 8 August read "8 Aug" and 14:00 read "14:00".
 *
 * Two things this deliberately does **not** touch. Instants are unchanged:
 * everything crossing the wire is still ISO UTC, and this is a rendering
 * decision only. And `<input type="datetime-local">` is left alone -- it speaks
 * the browser's wall clock, which for a UK desk is the same clock. Converting
 * it would only matter for somebody working abroad, which is not a case worth
 * the machinery.
 */

/** The one timezone this app displays in. */
export const DISPLAY_TIME_ZONE = "Europe/London";

/** The one locale this app formats in. British, because the desk is. */
export const DISPLAY_LOCALE = "en-GB";

/**
 * Formatters are expensive to construct and are built per render here, so they
 * are cached on their options. `Intl.DateTimeFormat` instances are immutable
 * and safe to share.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(options);

  let found = formatters.get(key);
  if (!found) {
    found = new Intl.DateTimeFormat(DISPLAY_LOCALE, {
      ...options,
      timeZone: DISPLAY_TIME_ZONE,
    });
    formatters.set(key, found);
  }

  return found;
}

/**
 * Format an instant in London.
 *
 * The one way anything in the app should turn a date into text. Exported so a
 * component needing a shape not covered below reaches for this rather than
 * `toLocaleString`, which would silently fall back to the viewer's zone.
 */
export function formatInZone(
  value: Date | string,
  options: Intl.DateTimeFormatOptions,
): string {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "";

  return formatter(options).format(date);
}

/**
 * The calendar year an instant falls in, **in London**.
 *
 * Not `date.getFullYear()`, which answers for the browser's zone. The two
 * disagree for a few hours either side of New Year -- an evening in London on
 * 31 December is already January in Sydney -- and the year suffix below would
 * appear or vanish accordingly.
 */
function yearIn(date: Date): number {
  return Number(formatter({ year: "numeric" }).format(date));
}

/**
 * Whether an instant falls in the current year, in London.
 *
 * Exported because half a dozen formatters across the app hide the year when it
 * is the current one, and each had worked it out with `getFullYear()` -- the
 * browser's year, not London's.
 */
export function isThisYear(value: Date | string): boolean {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return true;

  return yearIn(date) === yearIn(new Date());
}

/**
 * Compact relative timestamps for list rows ("3m", "2h", "Tue", "14 Mar").
 *
 * Rows are dense and the exact second never matters at a glance, so this trades
 * precision for scannability. The full timestamp is available in a title
 * attribute wherever this is used.
 *
 * The elapsed-time branches need no timezone at all: the gap between two
 * instants is the same number of seconds whoever is asking. Only the branches
 * that name a day or a date do, and those go through the formatters above.
 */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";

  const seconds = Math.round((Date.now() - then) / 1000);

  /*
   * Ahead of now. Nothing in the helpdesk is -- a message cannot arrive in the
   * future -- but the CRM's timeline carries booked meetings, and the negative
   * elapsed time fell through the "under a minute" branch and rendered a date
   * three weeks away as "now".
   */
  if (seconds < -60) {
    const ahead = -seconds;
    if (ahead < 3600) return `in ${Math.floor(ahead / 60)}m`;
    if (ahead < 86_400) return `in ${Math.floor(ahead / 3600)}h`;
    if (ahead < 7 * 86_400) return `in ${Math.floor(ahead / 86_400)}d`;
    return dayAndMonth(new Date(then));
  }

  if (seconds < 60) return "now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`;

  const date = new Date(then);

  // Within the last week, the weekday is more readable than a date.
  if (seconds < 7 * 86_400) {
    return formatInZone(date, { weekday: "short" });
  }

  return dayAndMonth(date);
}

/** "14 Mar", gaining a year only when it is not this one. */
function dayAndMonth(date: Date): string {
  return formatInZone(date, {
    day: "numeric",
    month: "short",
    ...(isThisYear(date) ? {} : { year: "numeric" }),
  });
}

/**
 * Date and time for the list view's "last email" column.
 *
 * Always both, and always the same shape, unlike relativeTime: the list is
 * scanned as a table and sorted on this value, and a column mixing "15:38" for
 * today with "07 Aug" for yesterday cannot be compared down its length -- nor
 * does it answer "when exactly", which is the question being asked of a column
 * headed Last email.
 *
 * The year appears only when it is not the current one, so the common case
 * stays narrow.
 */
export function shortDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  const day = formatInZone(date, {
    day: "2-digit",
    month: "short",
    ...(isThisYear(date) ? {} : { year: "2-digit" }),
  });

  const time = formatInZone(date, {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${day} ${time}`;
}

/** Full timestamp for tooltips and the conversation header. */
export function absoluteTime(iso: string): string {
  return formatInZone(iso, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * The wall-clock time an audit line happened, dropping today's date.
 *
 * "Reassigned by Charles at 13:30" is how the desk says this out loud, and it
 * is a different question from the one `relativeTime` answers. "2h" tells you
 * how long ago; only a clock time lets somebody line an action up against the
 * rest of their afternoon -- against the call they remember taking, or against
 * a customer's "I rang at half one and nobody had touched it".
 *
 * The date appears only when it is not today, and that variability -- which
 * `shortDateTime` deliberately refuses -- is right here for the opposite
 * reason. This renders inside a sentence rather than down a column, so there
 * is nothing to line up, and "on 14 Aug at 13:30" for every line of a
 * conversation opened this morning is noise repeated twenty times.
 *
 * Unlike `relativeTime` this never goes stale, so a thread left open over
 * lunch does not quietly start lying about when things happened.
 */
export function dayAndTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  const time = formatInZone(date, { hour: "2-digit", minute: "2-digit" });
  if (isToday(date)) return time;

  return `${dayAndMonth(date)}, ${time}`;
}

/**
 * Whether an instant falls on today's date **in London**, not the browser's.
 *
 * Compared as formatted day strings rather than by arithmetic on timestamps:
 * "today" is a calendar day in a zone that is sometimes UTC and sometimes
 * UTC+1, so there is no fixed number of milliseconds to divide by.
 */
function isToday(date: Date): boolean {
  const key = formatter({ year: "numeric", month: "2-digit", day: "2-digit" });
  return key.format(date) === key.format(new Date());
}

/**
 * How long ago something happened, as a phrase that reads in a sentence.
 *
 * `relativeTime` is built for dense list columns, where "3m" and "2h" are read
 * as measurements rather than as English. Dropped into prose it produces
 * "flagged 3m", and its under-a-minute case produces the genuinely wrong "now
 * ago" -- "now" is already a complete answer, so suffixing it contradicts
 * itself.
 *
 * So this expands the units and special-cases the one value that cannot take
 * the suffix. Anything a day or more old keeps the compact form, because at
 * that distance the reader wants the date, not "seventeen days ago".
 */
export function agoPhrase(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";

  const seconds = Math.round((Date.now() - then) / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  if (seconds < 86_400) {
    const hours = Math.floor(seconds / 3600);
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  return `${relativeTime(iso)} ago`;
}
